import apiService from './api';
import type { ApiResponse } from '../types';

export interface OrgPolicies {
  id: number;
  organisation_id: number;

  // Overtime: time-of-day
  overtime_start_hour: number;
  overtime_end_hour: number;
  overtime_multiplier: number;
  overtime_til_mode: boolean;

  // Overtime: Saturday
  saturday_all_overtime: boolean;
  saturday_multiplier: number;
  saturday_til_mode: boolean;

  // Overtime: Sunday
  sunday_all_overtime: boolean;
  sunday_multiplier: number;
  sunday_til_mode: boolean;

  // Period thresholds
  weekly_overtime_threshold_hours: number;
  fortnightly_overtime_threshold_hours: number;
  fortnightly_excess_category: 'Time in Lieu' | 'Overtime (1.5x)';

  // Lunch
  lunch_deduction_threshold_hours: number;

  // Public holiday
  ph_require_adjacent_workday: boolean;
  ph_noshow_action: 'skip_entry' | 'flag_review' | 'pay_anyway';

  // Sick / personal leave doctor's-certificate rules
  sick_cert_required_after_days: number;
  sick_cert_enforcement: 'warn_approver' | 'block_approval' | 'info_only';
  sick_cert_always_required: boolean;
  sick_cert_adjacent_to_weekend: boolean;
  sick_cert_adjacent_to_public_holiday: boolean;
  sick_cert_adjacent_to_other_leave: boolean;
  sick_cert_consecutive_split_detection: boolean;

  // Leave accrual
  annual_leave_accrual_hours: number;
  personal_leave_accrual_hours: number;
  long_service_leave_accrual_hours: number;
  annual_leave_max_balance_hours: number;
  personal_leave_max_balance_hours: number;
}

export type OrgPoliciesUpdate = Partial<Omit<OrgPolicies, 'id' | 'organisation_id'>>;

// Overtime fields shared across all three tiers
export interface OvertimeRules {
  overtime_start_hour: number;
  overtime_end_hour: number;
  overtime_multiplier: number;
  overtime_til_mode: boolean;
  saturday_all_overtime: boolean;
  saturday_multiplier: number;
  saturday_til_mode: boolean;
  sunday_all_overtime: boolean;
  sunday_multiplier: number;
  sunday_til_mode: boolean;
}

// Org-level per-pay-frequency overtime policy
export interface OrgOvertimePolicy extends OvertimeRules {
  id: number;
  organisation_id: number;
  pay_frequency: 'weekly' | 'fortnightly';
}

// Per-user overtime override
export interface UserOvertimeOverride extends OvertimeRules {
  id: number;
  user_id: number;
  organisation_id: number;
}

class OrgPolicyService {
  async getPolicies(orgId: number): Promise<ApiResponse<OrgPolicies>> {
    return apiService.get(`/organisations/${orgId}/policies`);
  }

  /**
   * Saving the work schedule or these settings re-issues any published policy
   * whose words they changed, and the response names them. A sentence for the
   * save message, or null when nobody is being asked again.
   */
  reissuedMessage(response: unknown): string | null {
    const titles = (response as { reissued_policies?: unknown })?.reissued_policies;
    if (!Array.isArray(titles) || titles.length === 0) return null;
    return `The wording of ${titles.join(', ')} changed with this, so staff will be asked to read and acknowledge it again.`;
  }

  async updatePolicies(orgId: number, data: OrgPoliciesUpdate): Promise<ApiResponse<OrgPolicies>> {
    return apiService.put(`/organisations/${orgId}/policies`, data);
  }

  async getOrgOvertimePolicy(orgId: number, payFrequency: 'weekly' | 'fortnightly'): Promise<ApiResponse<OrgOvertimePolicy | null>> {
    return apiService.get(`/organisations/${orgId}/overtime-policies/${payFrequency}`);
  }

  async upsertOrgOvertimePolicy(orgId: number, payFrequency: 'weekly' | 'fortnightly', data: OvertimeRules): Promise<ApiResponse<OrgOvertimePolicy>> {
    return apiService.put(`/organisations/${orgId}/overtime-policies/${payFrequency}`, data);
  }

  async deleteOrgOvertimePolicy(orgId: number, payFrequency: 'weekly' | 'fortnightly'): Promise<ApiResponse<null>> {
    return apiService.delete(`/organisations/${orgId}/overtime-policies/${payFrequency}`);
  }

  async getUserOvertimeOverride(userId: number): Promise<ApiResponse<UserOvertimeOverride | null>> {
    return apiService.get(`/users/${userId}/overtime-override`);
  }

  async upsertUserOvertimeOverride(userId: number, data: OvertimeRules): Promise<ApiResponse<UserOvertimeOverride>> {
    return apiService.put(`/users/${userId}/overtime-override`, data);
  }

  async deleteUserOvertimeOverride(userId: number): Promise<ApiResponse<null>> {
    return apiService.delete(`/users/${userId}/overtime-override`);
  }
}

const orgPolicyService = new OrgPolicyService();
export default orgPolicyService;
